import { User } from 'graphql.generated';
import { trackEvent, identifyUser } from '@zoopla/tracking';
import { Segment } from '../AuthModal.types';
import { getUser } from './getUser';

type TaxonomyPage =
    | '/homepage/'
    | '/home-values/'
    | '/valuation/'
    | '/privacy/'
    | '/discover/'
    | '/property/details/'
    | '/for-sale/results/'
    | '/for-sale/details/'
    | '/for-sale/contact/'
    | '/to-rent/results/'
    | '/to-rent/details/'
    | '/to-rent/contact/'
    | '/new-homes/results/'
    | '/new-homes/details/'
    | '/new-homes/contact/'
    | '/signin/'
    | '/register/'
    | '/schemes/help-to-buy/'
    | '/schemes/help-to-buy/scotland/'
    | '/schemes/help-to-buy/wales/'
    | '/schemes/shared-ownership/'
    | '/agent-portfolio/';

type TaxonomySection =
    | 'homepage'
    | 'home-values'
    | 'house-prices'
    | 'for-sale'
    | 'to-rent'
    | 'new-homes'
    | 'privacy'
    | 'discover'
    | 'signin'
    | 'register'
    | 'help-to-buy'
    | 'shared-ownership'
    | 'new homes/developers'
    | 'agent-portfolio';

type EventNamesList =
    | 'Search'
    | 'Search Result Impression'
    | 'Viewed Page'
    | 'Lead Sent'
    | 'Search Result Click'
    | 'Click Save Search'
    | 'Click Save Search Submitted'
    | 'Click Email Property Alert'
    | 'Click Email Property Alert Submitted'
    | 'Click Call Agent'
    | 'Click Email Agent'
    | 'Click Save Listing'
    | 'Click Book Agent Valuation'
    | 'Development Lead'
    | 'Share Content'
    | 'Email Marketing Opt-In'
    | 'Registration'
    | 'Sign In'
    | 'Listing Lead Sent'
    | 'Phone Click To Reveal';

export type BaseTaxonomy = {
    section: TaxonomySection;
    page: TaxonomyPage;
    pagePosition?: 'top';
    countryCode?:
        | 'gb'
        | 'es'
        | 'fr'
        | 'us'
        | 'pt'
        | 'it'
        | 'ie'
        | 'gr'
        | 'de'
        | 'za'
        | 'ch'
        | 'th'
        | 'tr'
        | 'cy'
        | 'ae';
    signedInStatus?: 'signed in' | 'signed out';
    brand: string;
    brandPrimary?: string;
    os?: string;
    platform?: 'desktop' | 'responsive' | 'mobile' | 'React';
    path?: string;
    listingsCategory?: 'residential' | 'development' | '';
    activity?:
        | 'listing_search'
        | 'listing_details'
        | 'listing_contact'
        | 'editorial'
        | 'development details page'
        | 'agent_developer_directory';
    aboutMeStatus?: string;
    device?: string;
    editorialFormat?: string;
    editorialTopic?: string[];
    sharePlatform?: 'facebook' | 'twitter' | 'copy link';
    userId?: string;
};

const getRegTaxonomy = (user: User, segment: Segment): BaseTaxonomy => {
    return {
        countryCode: 'gb',
        platform: 'React',
        brand: 'zoopla',
        userId: user.userUuid || '',
        brandPrimary: user.brandPrimary || '',
        aboutMeStatus: user.senderPropertyStatus || '',
        signedInStatus: user.loggedIn ? 'signed in' : 'signed out',
        registrationPlatform: user.registrationSources?.join(', '),
        registered: user.registered,
        userCreationDate: user.creationDate,
        section: segment?.section || 'unknown',
        page: segment?.page || 'unknown',
        activity: segment?.activity || 'unknown'
    } as BaseTaxonomy;
};

const getOptinTaxonomy = (user: User, segment: Segment): BaseTaxonomy => ({
    brand: 'zoopla',
    section: segment.section as BaseTaxonomy['section'],
    page: segment.page as BaseTaxonomy['page'],
    activity: segment.activity as BaseTaxonomy['activity'],
    userId: user.userUuid || ''
});

const eventTrackingMap: Record<string, EventNamesList> = {
    reg: 'Registration',
    signin: 'Sign In',
    optin: 'Email Marketing Opt-In'
};

export const trackAuthSegmentEvent = async (
    event: keyof typeof eventTrackingMap,
    segment: Segment | undefined,
    user?: User
) => {
    if (!segment) return;

    const userData = user ? user : await getUser();
    const eventName = eventTrackingMap[event];

    if (event === 'optin') {
        trackEvent(eventName, getOptinTaxonomy(userData || {}, segment));

        return;
    }

    if (userData) {
        const taxonomy = getRegTaxonomy(userData, segment);

        await identifyUser(window);
        trackEvent(eventName, taxonomy);
    }
};
